import React, { useState } from 'react';
import {
  normalizeHavvaAnalysis,
  formatConfidence,
  formatHavvaLabel,
  formatPercentage,
  formatHavvaDate,
  HAVVA_SCREENING_NOTE
} from '../utils/havvaAnalysis';

/**
 * Rendering of one HAVVA analysis, shared by the upload result panel, the
 * history cards, the history details modal and the Detail Analysis tab.
 *
 * Everything shown here comes from normalizeHavvaAnalysis(); nothing reads the
 * vendor's response fields. A field HAVVA did not send is left out.
 *
 * The confidence next to a label is the AI's confidence in that label. It is
 * NOT a share of healthy plants, so it never goes into the "% Healthy" bars -
 * those are kept for old-format results, which carry real percentages.
 */

// "Kailan · 99%" - or just the label when there is no confidence
const withConfidence = (label, confidence) =>
  [formatHavvaLabel(label), formatConfidence(confidence)].filter(Boolean).join(' · ');

const HEALTH_BADGE_CLASS = {
  healthy: 'bg-green-100 text-green-800',
  unhealthy: 'bg-red-100 text-red-800'
};

// Green for healthy, red for unhealthy; any other label is shown in grey
const HealthBadge = ({ health }) => (
  <span
    className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${HEALTH_BADGE_CLASS[health.status] || 'bg-gray-100 text-gray-700'}`}
    data-health-status={health.status || 'unknown'}
  >
    {formatHavvaLabel(health.label)}
  </span>
);

const HealthConfidence = ({ health, className }) => (
  formatConfidence(health.confidence)
    ? <span className={className}>{formatConfidence(health.confidence)} confidence</span>
    : null
);

const TextBlock = ({ title, children }) => (
  <div className="bg-gray-50 rounded-lg p-4">
    <div className="text-sm font-medium text-gray-900 mb-1">{title}</div>
    <p className="text-sm text-gray-700 whitespace-pre-line">{children}</p>
  </div>
);

/**
 * The new-format part of a result: crop, health, growth, summary,
 * explanations and recommended checks. Renders nothing for an old-format
 * result.
 *
 * compact - the history-card version: one line each for crop, health and
 *           growth, the summary cut to three lines and the number of
 *           recommended checks. The details modal shows everything.
 */
export const HavvaNewFormatResult = ({ analysis: rawAnalysis, compact = false }) => {
  const analysis = normalizeHavvaAnalysis(rawAnalysis);
  if (!analysis.isNewFormat) return null;

  const { crop, health, growth, summary, healthExplanation, growthExplanation, recommendedChecks } = analysis;

  if (compact) {
    return (
      <div className="space-y-1 text-xs" data-testid="havva-new-format-result">
        {crop && (
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Crop</span>
            <span className="font-medium text-gray-900 text-right">{withConfidence(crop.label, crop.confidence)}</span>
          </div>
        )}
        {health && (
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Health</span>
            <span className="flex items-center space-x-2">
              <HealthBadge health={health} />
              <HealthConfidence health={health} className="text-gray-600" />
            </span>
          </div>
        )}
        {growth && (
          <div>
            {(growth.label || formatConfidence(growth.confidence)) && (
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Growth</span>
                <span className="font-medium text-gray-900 text-right">{withConfidence(growth.label, growth.confidence)}</span>
              </div>
            )}
            {growth.note && (
              <p className="text-gray-500 line-clamp-2" title={growth.note}>{growth.note}</p>
            )}
          </div>
        )}
        {summary && (
          <div className="pt-2 border-t">
            <p className="text-gray-600 line-clamp-3">{summary}</p>
          </div>
        )}
        {recommendedChecks.length > 0 && (
          <p className="text-gray-600">
            {recommendedChecks.length} recommended check{recommendedChecks.length !== 1 ? 's' : ''}
          </p>
        )}
        <p className="text-gray-400">{HAVVA_SCREENING_NOTE}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="havva-new-format-result">
      {(crop || health || growth) && (
        <div className="grid grid-cols-2 gap-4">
          {crop && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500">Crop</div>
              <div className="font-medium">{withConfidence(crop.label, crop.confidence)}</div>
            </div>
          )}
          {health && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500">Health</div>
              <div className="flex flex-wrap items-center gap-2 mt-0.5">
                <HealthBadge health={health} />
                <HealthConfidence health={health} className="text-sm text-gray-700" />
              </div>
            </div>
          )}
          {growth && (
            <div className="bg-gray-50 rounded-lg p-4 col-span-2">
              <div className="text-sm text-gray-500">Growth</div>
              {(growth.label || formatConfidence(growth.confidence)) && (
                <div className="font-medium">{withConfidence(growth.label, growth.confidence)}</div>
              )}
              {growth.note && <p className="text-xs text-gray-500 mt-1">{growth.note}</p>}
            </div>
          )}
        </div>
      )}

      {summary && <TextBlock title="Summary">{summary}</TextBlock>}
      {healthExplanation && <TextBlock title="Health explanation">{healthExplanation}</TextBlock>}
      {growthExplanation && <TextBlock title="Growth explanation">{growthExplanation}</TextBlock>}

      {recommendedChecks.length > 0 && (
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-sm font-medium text-blue-900 mb-1">Recommended checks</div>
          <ol className="list-decimal list-outside pl-5 space-y-1 text-sm text-blue-800">
            {recommendedChecks.map((check, index) => (
              <li key={index}>{check}</li>
            ))}
          </ol>
        </div>
      )}

      <p className="text-xs text-gray-500">{HAVVA_SCREENING_NOTE}</p>
    </div>
  );
};

/**
 * One stored analysis in full: image, camera, time, the result and the file.
 * Body of the history "Analysis Details" modal; also used by the Detail
 * Analysis tab for the latest stored analysis of a camera.
 *
 * An old-format result (real percentages, recommendedActivity) is laid out
 * exactly as before the new format existed.
 */
export const HavvaAnalysisDetails = ({ analysis: rawAnalysis }) => {
  const analysis = normalizeHavvaAnalysis(rawAnalysis);
  // An image that does not load (a vendor URL that has expired or is gone) is
  // left out together with its link, instead of showing a broken picture
  const [failedImage, setFailedImage] = useState(null);

  return (
    <>
      {/* Annotated Image */}
      {analysis.annotatedImage && analysis.annotatedImage !== failedImage && (
        <div className="mb-6">
          <img 
            src={analysis.annotatedImage} 
            alt="Annotated analysis"
            className="w-full rounded-lg border"
            onError={() => setFailedImage(analysis.annotatedImage)}
          />
          <a 
            href={analysis.annotatedImage}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block text-sm text-blue-600 hover:text-blue-800"
          >
            Open full image ↗
          </a>
        </div>
      )}

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-500">Camera ID</div>
          <div className="font-medium">{analysis.cameraId}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-500">Analyzed At</div>
          <div className="font-medium">{formatHavvaDate(analysis.timeStamp, true)}</div>
        </div>
        {/* Healthy / unhealthy share - only when HAVVA returned real percentages */}
        {analysis.hasPercentages && (
          <>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-600">Healthy</div>
              <div className="text-2xl font-bold text-green-700">{formatPercentage(analysis.healthyPercentage)}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-sm text-red-600">Unhealthy</div>
              <div className="text-2xl font-bold text-red-700">{formatPercentage(analysis.unhealthyPercentage)}</div>
            </div>
          </>
        )}
      </div>

      {/* New-format result */}
      {analysis.isNewFormat && (
        <div className="mb-4">
          <HavvaNewFormatResult analysis={analysis} />
        </div>
      )}

      {/* Recommendation (old format; a new-format result has its checks above) */}
      {(!analysis.isNewFormat || analysis.recommendedActivity) && (
        <div className="bg-blue-50 rounded-lg p-4 mb-4">
          <div className="text-sm font-medium text-blue-900 mb-1">AI Recommendation</div>
          <div className="text-blue-800">{analysis.recommendedActivity || 'No recommendation available'}</div>
        </div>
      )}

      {/* Additional Info */}
      <div className="text-xs text-gray-500 space-y-1">
        <div>📁 File: {analysis.fileName}</div>
        <div>🆔 Analysis ID: {analysis.id}</div>
      </div>
    </>
  );
};

export default HavvaAnalysisDetails;
